import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import { 
  Calculator, Package, Banknote, Clock, FileText, CreditCard, Plus, Trash2, 
  Edit, Settings, Save, DollarSign, Percent, Users, Truck, MessageSquare,
  Building2, CheckCircle, AlertCircle, Eye, Printer, MapPin, Calendar, Phone, Mail, Loader2
} from 'lucide-react';
import api from '../../utils/api';

const EstimateSettings = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  
  // Preview state
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Distance calculator state
  const [testFromAddress, setTestFromAddress] = useState('');
  const [testToAddress, setTestToAddress] = useState('');
  const [testStorageAddress, setTestStorageAddress] = useState('');
  const [includeStorageInTest, setIncludeStorageInTest] = useState(false);
  const [calculatingDistance, setCalculatingDistance] = useState(false);
  const [distanceResult, setDistanceResult] = useState(null);
  
  // Dialog states
  const [showLabourDialog, setShowLabourDialog] = useState(false);
  const [showChargeDialog, setShowChargeDialog] = useState(false);
  const [showMaterialDialog, setShowMaterialDialog] = useState(false);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  
  // Edit mode tracking
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [editingLabourId, setEditingLabourId] = useState(null);
  const [editingChargeId, setEditingChargeId] = useState(null);
  
  // Form states
  const [labourForm, setLabourForm] = useState({ name: '', rate_per_hour: 0, min_hours: 2, description: '', is_default: false });
  const [chargeForm, setChargeForm] = useState({ name: '', amount: 0, charge_type: 'fixed', description: '' });
  const [materialForm, setMaterialForm] = useState({ name: '', price: 0, description: '', includes: '' });
  const [productForm, setProductForm] = useState({ name: '', price: 0, category: 'service', description: '' });
  const [messageForm, setMessageForm] = useState({ title: '', content: '', message_type: 'standard', position: 'footer', is_default: false });
  const [paymentForm, setPaymentForm] = useState({ name: '', icon: 'credit-card', description: '', instructions: '' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/company/estimate-settings');
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load estimate settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchPreview = async () => {
    setLoadingPreview(true);
    try {
      const response = await api.post('/company/estimate/preview', { sample_data: true });
      setPreviewData(response.data);
      setShowPreviewDialog(true);
    } catch (error) {
      toast.error('Failed to generate preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const saveGeneralSettings = async () => {
    setSaving(true);
    try {
      await api.put('/company/estimate-settings', {
        default_tax_rate: settings.default_tax_rate,
        default_deposit_percentage: settings.default_deposit_percentage,
        default_validity_days: settings.default_validity_days,
        currency: settings.currency,
        currency_symbol: settings.currency_symbol,
        show_item_breakdown: settings.show_item_breakdown,
        show_labour_breakdown: settings.show_labour_breakdown,
        show_company_logo: settings.show_company_logo,
        show_payment_methods: settings.show_payment_methods,
        auto_calculate_volume: settings.auto_calculate_volume,
        volume_rate_per_cubic_meter: settings.volume_rate_per_cubic_meter,
        // Distance pricing
        distance_pricing_enabled: settings.distance_pricing_enabled,
        distance_unit: settings.distance_unit,
        distance_rate_per_mile: settings.distance_rate_per_mile,
        distance_rate_per_km: settings.distance_rate_per_km,
        minimum_distance_charge: settings.minimum_distance_charge,
        free_distance_miles: settings.free_distance_miles,
        free_distance_km: settings.free_distance_km,
        // Storage pricing
        storage_pricing_enabled: settings.storage_pricing_enabled,
        storage_pricing_model: settings.storage_pricing_model,
        storage_box_volume_m3: settings.storage_box_volume_m3,
        storage_rate_per_box_daily: settings.storage_rate_per_box_daily,
        storage_rate_per_box_weekly: settings.storage_rate_per_box_weekly,
        storage_rate_per_box_monthly: settings.storage_rate_per_box_monthly,
        storage_rate_per_m3_daily: settings.storage_rate_per_m3_daily,
        storage_rate_per_m3_weekly: settings.storage_rate_per_m3_weekly,
        storage_rate_per_m3_monthly: settings.storage_rate_per_m3_monthly,
        storage_container_sizes: settings.storage_container_sizes,
        storage_minimum_days: settings.storage_minimum_days,
        storage_free_days: settings.free_days,
        // Storage Handling Charges
        storage_handling_enabled: settings.storage_handling_enabled,
        storage_handling_per_volume: settings.storage_handling_per_volume,
        storage_unload_charge: settings.storage_unload_charge,
        storage_reload_charge: settings.storage_reload_charge,
        storage_unload_rate_per_m3: settings.storage_unload_rate_per_m3,
        storage_reload_rate_per_m3: settings.storage_reload_rate_per_m3,
        // Packing settings
        packing_per_item_fee: settings.packing_per_item_fee,
        packing_fragile_surcharge: settings.packing_fragile_surcharge,
        packing_large_item_surcharge: settings.packing_large_item_surcharge,
        packing_time_small: settings.packing_time_small,
        packing_time_medium: settings.packing_time_medium,
        packing_time_large: settings.packing_time_large,
        packing_time_fragile: settings.packing_time_fragile
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Labour Rate handlers
  const addLabourRate = async () => {
    try {
      await api.post('/company/estimate-settings/labour-rate', {
        ...labourForm,
        id: `rate-${Date.now()}`,
        is_active: true
      });
      toast.success('Labour rate added');
      setShowLabourDialog(false);
      setLabourForm({ name: '', rate_per_hour: 0, min_hours: 2, description: '', is_default: false });
      fetchSettings();
    } catch (error) {
      toast.error('Failed to add labour rate');
    }
  };

  const deleteLabourRate = async (rateId) => {
    try {
      await api.delete(`/company/estimate-settings/labour-rate/${rateId}`);
      toast.success('Labour rate deleted');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to delete labour rate');
    }
  };

  // Test Distance Calculator
  const calculateTestDistance = async () => {
    if (!testFromAddress || !testToAddress) {
      toast.error('Please enter both From and To addresses');
      return;
    }
    
    setCalculatingDistance(true);
    setDistanceResult(null);
    
    try {
      // If including storage, we need to calculate two legs
      if (includeStorageInTest && testStorageAddress) {
        // Leg 1: From → Storage
        const leg1Response = await api.post('/company/calculate-distance', {
          from_address: testFromAddress,
          to_address: testStorageAddress
        });
        
        // Leg 2: Storage → To
        const leg2Response = await api.post('/company/calculate-distance', {
          from_address: testStorageAddress,
          to_address: testToAddress
        });
        
        const leg1 = leg1Response.data;
        const leg2 = leg2Response.data;
        
        // Calculate totals
        const totalMiles = (leg1.distance_miles || 0) + (leg2.distance_miles || 0);
        const totalKm = (leg1.distance_km || 0) + (leg2.distance_km || 0);
        const totalMinutes = (leg1.duration_minutes || 0) + (leg2.duration_minutes || 0);
        
        // Calculate cost based on settings
        const unit = settings?.distance_unit || 'miles';
        const rate = unit === 'miles' ? (settings?.distance_rate_per_mile || 1.50) : (settings?.distance_rate_per_km || 0.95);
        const freeDistance = unit === 'miles' ? (settings?.free_distance_miles || 0) : (settings?.free_distance_km || 0);
        const distance = unit === 'miles' ? totalMiles : totalKm;
        const chargeableDistance = Math.max(0, distance - freeDistance);
        let cost = chargeableDistance * rate;
        const minCharge = settings?.minimum_distance_charge || 0;
        if (cost > 0 && cost < minCharge) cost = minCharge;
        
        setDistanceResult({
          multiLeg: true,
          legs: [
            { from: 'Pickup', to: 'Storage', ...leg1 },
            { from: 'Storage', to: 'Delivery', ...leg2 }
          ],
          total_miles: totalMiles.toFixed(2),
          total_km: totalKm.toFixed(2),
          total_duration_minutes: totalMinutes,
          chargeable_distance: chargeableDistance.toFixed(2),
          estimated_cost: cost.toFixed(2),
          unit: unit,
          rate: rate
        });
      } else {
        // Direct route: From → To
        const response = await api.post('/company/calculate-distance', {
          from_address: testFromAddress,
          to_address: testToAddress
        });
        
        const data = response.data;
        
        // Calculate cost based on settings
        const unit = settings?.distance_unit || 'miles';
        const rate = unit === 'miles' ? (settings?.distance_rate_per_mile || 1.50) : (settings?.distance_rate_per_km || 0.95);
        const freeDistance = unit === 'miles' ? (settings?.free_distance_miles || 0) : (settings?.free_distance_km || 0);
        const distance = unit === 'miles' ? data.distance_miles : data.distance_km;
        const chargeableDistance = Math.max(0, distance - freeDistance);
        let cost = chargeableDistance * rate;
        const minCharge = settings?.minimum_distance_charge || 0;
        if (cost > 0 && cost < minCharge) cost = minCharge;
        
        setDistanceResult({
          multiLeg: false,
          ...data,
          chargeable_distance: chargeableDistance.toFixed(2),
          estimated_cost: cost.toFixed(2),
          unit: unit,
          rate: rate
        });
      }
      
      toast.success('Distance calculated successfully!');
    } catch (error) {
      console.error('Distance calculation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to calculate distance. Check your Google Maps API key.');
    } finally {
      setCalculatingDistance(false);
    }
  };

  // Additional Charge handlers
  const addCharge = async () => {
    try {
      await api.post('/company/estimate-settings/additional-charge', {
        ...chargeForm,
        id: `charge-${Date.now()}`,
        is_active: true
      });
      toast.success('Charge added');
      setShowChargeDialog(false);
      setChargeForm({ name: '', amount: 0, charge_type: 'fixed', description: '' });
      fetchSettings();
    } catch (error) {
      toast.error('Failed to add charge');
    }
  };

  const deleteCharge = async (chargeId) => {
    try {
      await api.delete(`/company/estimate-settings/additional-charge/${chargeId}`);
      toast.success('Charge deleted');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to delete charge');
    }
  };

  // Packing Material handlers
  const addMaterial = async () => {
    try {
      const includes = materialForm.includes.split(',').map(s => s.trim()).filter(Boolean);
      await api.post('/company/estimate-settings/packing-material', {
        ...materialForm,
        includes,
        id: `material-${Date.now()}`,
        is_active: true
      });
      toast.success('Packing material added');
      setShowMaterialDialog(false);
      setMaterialForm({ name: '', price: 0, description: '', includes: '' });
      fetchSettings();
    } catch (error) {
      toast.error('Failed to add material');
    }
  };

  const deleteMaterial = async (materialId) => {
    try {
      await api.delete(`/company/estimate-settings/packing-material/${materialId}`);
      toast.success('Material deleted');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to delete material');
    }
  };

  // Custom Product handlers
  const addProduct = async () => {
    try {
      await api.post('/company/estimate-settings/custom-product', {
        ...productForm,
        id: `product-${Date.now()}`,
        is_active: true
      });
      toast.success('Product added');
      setShowProductDialog(false);
      setProductForm({ name: '', price: 0, category: 'service', description: '' });
      fetchSettings();
    } catch (error) {
      toast.error('Failed to add product');
    }
  };

  const deleteProduct = async (productId) => {
    try {
      await api.delete(`/company/estimate-settings/custom-product/${productId}`);
      toast.success('Product deleted');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  // Message handlers
  const addMessage = async () => {
    try {
      await api.post('/company/estimate-settings/message', {
        ...messageForm,
        id: `msg-${Date.now()}`,
        is_active: true
      });
      toast.success('Message added');
      setShowMessageDialog(false);
      setMessageForm({ title: '', content: '', message_type: 'standard', position: 'footer', is_default: false });
      fetchSettings();
    } catch (error) {
      toast.error('Failed to add message');
    }
  };

  const deleteMessage = async (messageId) => {
    try {
      await api.delete(`/company/estimate-settings/message/${messageId}`);
      toast.success('Message deleted');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  // Payment Method handlers
  const addPaymentMethod = async () => {
    try {
      await api.post('/company/estimate-settings/payment-method', {
        ...paymentForm,
        id: `pay-${Date.now()}`,
        is_active: true
      });
      toast.success('Payment method added');
      closePaymentDialog();
      fetchSettings();
    } catch (error) {
      toast.error('Failed to add payment method');
    }
  };

  const updatePaymentMethod = async () => {
    if (!editingPaymentId) return;
    try {
      await api.put(`/company/estimate-settings/payment-method/${editingPaymentId}`, {
        ...paymentForm,
        is_active: true
      });
      toast.success('Payment method updated');
      closePaymentDialog();
      fetchSettings();
    } catch (error) {
      toast.error('Failed to update payment method');
    }
  };

  const openEditPaymentDialog = (method) => {
    setPaymentForm({
      name: method.name || '',
      icon: method.icon || 'credit-card',
      description: method.description || '',
      instructions: method.instructions || ''
    });
    setEditingPaymentId(method.id);
    setShowPaymentDialog(true);
  };

  const closePaymentDialog = () => {
    setShowPaymentDialog(false);
    setEditingPaymentId(null);
    setPaymentForm({ name: '', icon: 'credit-card', description: '', instructions: '' });
  };

  const deletePaymentMethod = async (methodId) => {
    try {
      await api.delete(`/company/estimate-settings/payment-method/${methodId}`);
      toast.success('Payment method deleted');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to delete payment method');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="estimate-settings-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">{t('estimate_settings.title', 'Estimate Settings')}</h1>
          <p className="text-gray-600">{t('estimate_settings.subtitle', 'Configure pricing, labour rates, and estimate templates')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchPreview} disabled={loadingPreview} data-testid="preview-btn">
            <Eye size={16} className="mr-2" />
            {loadingPreview ? t('common.loading', 'Loading...') : t('estimate_settings.preview.generate', 'Preview Estimate')}
          </Button>
          <Button onClick={saveGeneralSettings} disabled={saving} data-testid="save-settings-btn">
            <Save size={16} className="mr-2" />
            {saving ? t('estimate_settings.actions.saving', 'Saving...') : t('estimate_settings.actions.save_settings', 'Save Settings')}
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="overflow-x-auto pb-2" style={{ scrollbarWidth: 'thin' }}>
          <TabsList className="inline-flex w-max gap-1 p-1 bg-muted/50">
            <TabsTrigger value="general" data-testid="tab-general" className="whitespace-nowrap text-xs px-2 py-1.5">
              <Settings size={14} className="mr-1" />
              {t('estimate_settings.tabs.general', 'General')}
            </TabsTrigger>
            <TabsTrigger value="distance" data-testid="tab-distance" className="whitespace-nowrap text-xs px-2 py-1.5">
              <MapPin size={14} className="mr-1" />
              Distance
            </TabsTrigger>
            <TabsTrigger value="storage" data-testid="tab-storage" className="whitespace-nowrap text-xs px-2 py-1.5">
              <Building2 size={14} className="mr-1" />
              Storage
            </TabsTrigger>
            <TabsTrigger value="labour" data-testid="tab-labour" className="whitespace-nowrap text-xs px-2 py-1.5">
              <Users size={14} className="mr-1" />
            {t('estimate_settings.tabs.labour', 'Labour Rates')}
          </TabsTrigger>
          <TabsTrigger value="charges" data-testid="tab-charges" className="whitespace-nowrap text-xs px-2 py-1.5">
            <DollarSign size={14} className="mr-1" />
            {t('estimate_settings.tabs.charges', 'Additional Charges')}
          </TabsTrigger>
          <TabsTrigger value="materials" data-testid="tab-materials" className="whitespace-nowrap text-xs px-2 py-1.5">
            <Package size={14} className="mr-1" />
            {t('estimate_settings.tabs.materials', 'Packing Materials')}
          </TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages" className="whitespace-nowrap text-xs px-2 py-1.5">
            <MessageSquare size={14} className="mr-1" />
            {t('estimate_settings.tabs.messages', 'Messages')}
          </TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments" className="whitespace-nowrap text-xs px-2 py-1.5">
            <CreditCard size={14} className="mr-1" />
            {t('estimate_settings.tabs.payments', 'Payment Methods')}
          </TabsTrigger>
          <TabsTrigger value="packing" data-testid="tab-packing" className="whitespace-nowrap text-xs px-2 py-1.5">
            <Package size={14} className="mr-1" />
            Packing Services
          </TabsTrigger>
          </TabsList>
        </div>

        {/* General Settings Tab */}
        <TabsContent value="general">
          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="h-fit">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Calculator size={18} />
                  Default Values
                </CardTitle>
                <CardDescription className="text-xs">Set default values for new estimates</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">VAT Rate (%)</Label>
                    <Input
                      type="number"
                      className="h-8 text-sm"
                      value={settings?.default_tax_rate || 0}
                      onChange={(e) => setSettings({...settings, default_tax_rate: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Deposit (%)</Label>
                    <Input
                      type="number"
                      className="h-8 text-sm"
                      value={settings?.default_deposit_percentage || 0}
                      onChange={(e) => setSettings({...settings, default_deposit_percentage: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Quote Validity (days)</Label>
                    <Input
                      type="number"
                      className="h-8 text-sm"
                      value={settings?.default_validity_days || 30}
                      onChange={(e) => setSettings({...settings, default_validity_days: parseInt(e.target.value) || 30})}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Currency Symbol</Label>
                    <Input
                      className="h-8 text-sm"
                      value={settings?.currency_symbol || '£'}
                      onChange={(e) => setSettings({...settings, currency_symbol: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Volume Rate (per m³)</Label>
                  <Input
                    type="number"
                    className="h-8 text-sm"
                    value={settings?.volume_rate_per_cubic_meter || 50}
                    onChange={(e) => setSettings({...settings, volume_rate_per_cubic_meter: parseFloat(e.target.value) || 50})}
                  />
                  <p className="text-xs text-gray-500 mt-1">Used for auto-calculating prices from inventory volume</p>
                </div>
              </CardContent>
            </Card>

            <Card className="h-fit">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <FileText size={18} />
                  Display Options
                </CardTitle>
                <CardDescription className="text-xs">Control what appears on estimates</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 pt-0">
                <div className="flex items-center justify-between p-2 border rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Show Item Breakdown</p>
                    <p className="text-xs text-gray-500">List individual inventory items</p>
                  </div>
                  <Switch
                    checked={settings?.show_item_breakdown ?? true}
                    onCheckedChange={(val) => setSettings({...settings, show_item_breakdown: val})}
                  />
                </div>
                <div className="flex items-center justify-between p-2 border rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Show Labour Breakdown</p>
                    <p className="text-xs text-gray-500">Display hours and rates</p>
                  </div>
                  <Switch
                    checked={settings?.show_labour_breakdown ?? true}
                    onCheckedChange={(val) => setSettings({...settings, show_labour_breakdown: val})}
                  />
                </div>
                <div className="flex items-center justify-between p-2 border rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Show Company Logo</p>
                    <p className="text-xs text-gray-500">Include your logo on estimates</p>
                  </div>
                  <Switch
                    checked={settings?.show_company_logo ?? true}
                    onCheckedChange={(val) => setSettings({...settings, show_company_logo: val})}
                  />
                </div>
                <div className="flex items-center justify-between p-2 border rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Show Payment Methods</p>
                    <p className="text-xs text-gray-500">Display accepted payment options</p>
                  </div>
                  <Switch
                    checked={settings?.show_payment_methods ?? true}
                    onCheckedChange={(val) => setSettings({...settings, show_payment_methods: val})}
                  />
                </div>
                <div className="flex items-center justify-between p-2 border rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Auto-Calculate from Volume</p>
                    <p className="text-xs text-gray-500">Use inventory volume for pricing</p>
                  </div>
                  <Switch
                    checked={settings?.auto_calculate_volume ?? true}
                    onCheckedChange={(val) => setSettings({...settings, auto_calculate_volume: val})}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Distance Pricing Tab */}
        <TabsContent value="distance">
          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="h-fit">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <MapPin size={18} />
                  Distance Pricing
                </CardTitle>
                <CardDescription className="text-xs">
                  Calculate charges based on driving distance between pickup and delivery
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                <div className="flex items-center justify-between p-2 border rounded-lg bg-blue-50">
                  <div>
                    <p className="font-medium text-sm">Enable Distance Pricing</p>
                    <p className="text-xs text-gray-500">Auto-calculate distance costs</p>
                  </div>
                  <Switch
                    checked={settings?.distance_pricing_enabled ?? false}
                    onCheckedChange={(val) => setSettings({...settings, distance_pricing_enabled: val})}
                  />
                </div>
                
                {settings?.distance_pricing_enabled && (
                  <>
                    <div>
                      <Label className="text-xs">Distance Unit</Label>
                      <Select
                        value={settings?.distance_unit || 'miles'}
                        onValueChange={(val) => setSettings({...settings, distance_unit: val})}
                      >
                        <SelectTrigger className="mt-1 h-8 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="miles">Miles (UK/US)</SelectItem>
                          <SelectItem value="km">Kilometers (EU)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Rate per Mile ({settings?.currency_symbol || '£'})</Label>
                        <Input
                          type="number"
                          step="0.01"
                          className="h-8 text-sm"
                          value={settings?.distance_rate_per_mile || 1.50}
                          onChange={(e) => setSettings({...settings, distance_rate_per_mile: parseFloat(e.target.value) || 0})}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Rate per Km ({settings?.currency_symbol || '£'})</Label>
                        <Input
                          type="number"
                          step="0.01"
                          className="h-8 text-sm"
                          value={settings?.distance_rate_per_km || 0.95}
                          onChange={(e) => setSettings({...settings, distance_rate_per_km: parseFloat(e.target.value) || 0})}
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Free Miles (included)</Label>
                        <Input
                          type="number"
                          className="h-8 text-sm"
                          value={settings?.free_distance_miles || 0}
                          onChange={(e) => setSettings({...settings, free_distance_miles: parseFloat(e.target.value) || 0})}
                        />
                        <p className="text-xs text-gray-500 mt-1">First X miles free</p>
                      </div>
                      <div>
                        <Label className="text-xs">Free Km (included)</Label>
                        <Input
                          type="number"
                          className="h-8 text-sm"
                          value={settings?.free_distance_km || 0}
                          onChange={(e) => setSettings({...settings, free_distance_km: parseFloat(e.target.value) || 0})}
                        />
                        <p className="text-xs text-gray-500 mt-1">First X km free</p>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-xs">Minimum Distance Charge ({settings?.currency_symbol || '£'})</Label>
                      <Input
                        type="number"
                        step="0.01"
                        className="h-8 text-sm"
                        value={settings?.minimum_distance_charge || 0}
                        onChange={(e) => setSettings({...settings, minimum_distance_charge: parseFloat(e.target.value) || 0})}
                      />
                      <p className="text-xs text-gray-500 mt-1">Minimum charge if distance pricing applies</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
            
            {/* Test Distance Calculator */}
            <Card className="h-fit">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Calculator size={18} />
                  Test Distance Calculator
                </CardTitle>
                <CardDescription className="text-xs">
                  Test your distance pricing with real addresses
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                <div className="space-y-3">
                  <div>
                    <Label>From Address (Pickup)</Label>
                    <AddressAutocomplete
                      value={testFromAddress}
                      onChange={setTestFromAddress}
                      onPlaceSelect={(place) => {
                        setTestFromAddress(place.formatted_address);
                      }}
                      placeholder="Start typing pickup address..."
                      className="mt-1"
                    />
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                    <Switch
                      checked={includeStorageInTest}
                      onCheckedChange={setIncludeStorageInTest}
                    />
                    <div>
                      <p className="font-medium text-sm">Include Storage Stop</p>
                      <p className="text-xs text-gray-500">Test multi-leg journey: Pickup → Storage → Delivery</p>
                    </div>
                  </div>
                  
                  {includeStorageInTest && (
                    <div>
                      <Label>Storage Facility Address</Label>
                      <AddressAutocomplete
                        value={testStorageAddress || (settings?.storage_facility_address ? 
                          `${settings.storage_facility_address.street || ''}, ${settings.storage_facility_address.city || ''}, ${settings.storage_facility_address.postal_code || ''}` : '')}
                        onChange={setTestStorageAddress}
                        onPlaceSelect={(place) => {
                          setTestStorageAddress(place.formatted_address);
                        }}
                        placeholder="Start typing storage address..."
                        className="mt-1"
                      />
                      {settings?.storage_facility_name && (
                        <p className="text-xs text-green-600 mt-1">
                          ✓ Using saved facility: {settings.storage_facility_name}
                        </p>
                      )}
                    </div>
                  )}
                  
                  <div>
                    <Label>To Address (Delivery)</Label>
                    <AddressAutocomplete
                      value={testToAddress}
                      onChange={setTestToAddress}
                      onPlaceSelect={(place) => {
                        setTestToAddress(place.formatted_address);
                      }}
                      placeholder="Start typing delivery address..."
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <Button 
                  onClick={calculateTestDistance}
                  disabled={calculatingDistance || !testFromAddress || !testToAddress}
                  className="w-full"
                >
                  {calculatingDistance ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <MapPin className="mr-2 h-4 w-4" />
                      Calculate Distance
                    </>
                  )}
                </Button>
                
                {/* Results Display */}
                {distanceResult && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg space-y-3">
                    <h4 className="font-semibold text-green-800 flex items-center gap-2">
                      <CheckCircle size={18} />
                      Distance Calculation Results
                    </h4>
                    
                    {distanceResult.multiLeg ? (
                      <>
                        <div className="space-y-2">
                          {distanceResult.legs?.map((leg, idx) => (
                            <div key={idx} className="flex justify-between items-center text-sm p-2 bg-white rounded">
                              <span className="font-medium">{leg.from} → {leg.to}</span>
                              <span>{leg.distance_text || `${leg.distance_miles?.toFixed(1)} miles`}</span>
                            </div>
                          ))}
                        </div>
                        <Separator />
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-gray-500">Total Distance</p>
                            <p className="font-bold text-lg">{distanceResult.total_miles} miles</p>
                            <p className="text-xs text-gray-400">({distanceResult.total_km} km)</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Total Duration</p>
                            <p className="font-bold text-lg">{distanceResult.total_duration_minutes} mins</p>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-gray-500">Distance</p>
                          <p className="font-bold text-lg">{distanceResult.distance_miles} miles</p>
                          <p className="text-xs text-gray-400">({distanceResult.distance_km} km)</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Duration</p>
                          <p className="font-bold text-lg">{distanceResult.duration_text || `${distanceResult.duration_minutes} mins`}</p>
                        </div>
                      </div>
                    )}
                    
                    <Separator />
                    
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-gray-500 text-sm">Pricing Calculation</p>
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-sm">
                          Chargeable: {distanceResult.chargeable_distance} {distanceResult.unit} × {settings?.currency_symbol || '£'}{distanceResult.rate}/{distanceResult.unit?.slice(0, -1)}
                        </span>
                        <span className="font-bold text-xl text-green-700">
                          {settings?.currency_symbol || '£'}{distanceResult.estimated_cost}
                        </span>
                      </div>
                      {settings?.free_distance_miles > 0 || settings?.free_distance_km > 0 ? (
                        <p className="text-xs text-gray-400 mt-1">
                          (First {settings?.distance_unit === 'km' ? settings?.free_distance_km : settings?.free_distance_miles} {settings?.distance_unit || 'miles'} free)
                        </p>
                      ) : null}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck size={20} />
                  How It Works
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="p-1 bg-blue-100 rounded-full text-blue-600 mt-1">1</div>
                    <div>
                      <p className="font-medium">Automatic Calculation</p>
                      <p className="text-sm text-gray-500">When generating an estimate, the system uses Google Maps to calculate the actual driving distance between pickup and delivery addresses.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="p-1 bg-blue-100 rounded-full text-blue-600 mt-1">2</div>
                    <div>
                      <p className="font-medium">Multi-leg Journey</p>
                      <p className="text-sm text-gray-500">If storage is required, distance is calculated as: Pickup → Storage Facility → Delivery (two legs).</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="p-1 bg-blue-100 rounded-full text-blue-600 mt-1">3</div>
                    <div>
                      <p className="font-medium">Shown on Estimate</p>
                      <p className="text-sm text-gray-500">Distance details appear in the estimate breakdown so customers can see the journey distance and associated cost.</p>
                    </div>
                  </div>
                </div>
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> Google Maps API must be configured for distance calculation to work. Contact support if you need assistance.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Storage Pricing Tab */}
        <TabsContent value="storage">
          <div className="space-y-4">
            {/* Main Storage Settings Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Building2 size={18} />
                  Storage Pricing
                </CardTitle>
                <CardDescription className="text-xs">
                  Configure storage charges for customers needing temporary storage
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 pt-0">
                <div className="flex items-center justify-between p-2 border rounded-lg bg-green-50">
                  <div>
                    <p className="font-medium text-sm">Enable Storage Pricing</p>
                    <p className="text-xs text-gray-500">Calculate storage costs on estimates</p>
                  </div>
                  <Switch
                    checked={settings?.storage_pricing_enabled ?? false}
                    onCheckedChange={(val) => setSettings({...settings, storage_pricing_enabled: val})}
                  />
                </div>
                
                {settings?.storage_pricing_enabled && (
                  <>
                    <Separator className="my-3" />
                    
                    {/* Pricing Model Selection - Now with rates directly below */}
                    <div className="p-3 border-2 border-primary/30 rounded-lg bg-primary/5">
                      <h4 className="font-semibold mb-2 flex items-center gap-2 text-primary text-sm">
                        <Calculator size={16} />
                        Pricing Model & Rates
                      </h4>
                      
                      <div className="mb-3">
                        <Label className="text-xs">Select Pricing Model</Label>
                        <Select
                          value={settings?.storage_pricing_model || 'per_box'}
                          onValueChange={(val) => setSettings({...settings, storage_pricing_model: val})}
                        >
                          <SelectTrigger className="mt-1 bg-white h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="per_box">Per Storage Box/Container (e.g., 7m³)</SelectItem>
                            <SelectItem value="per_volume">Per Cubic Meter (m³)</SelectItem>
                            <SelectItem value="per_container">Per Fixed Container Size</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Per Box Rates - shown when per_box selected */}
                      {settings?.storage_pricing_model === 'per_box' && (
                        <div className="p-3 bg-white rounded-lg border space-y-3">
                          <div className="flex items-center gap-2 text-xs font-medium text-blue-700">
                            <Package size={14} />
                            Per Box Rates
                          </div>
                          <div>
                            <Label className="text-xs">Box/Container Volume (m³)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              className="h-8 text-sm"
                              value={settings?.storage_box_volume_m3 || 7}
                              onChange={(e) => setSettings({...settings, storage_box_volume_m3: parseFloat(e.target.value) || 7})}
                            />
                            <p className="text-xs text-gray-500 mt-1">Standard wooden storage box holds ~7m³</p>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <Label className="text-xs">Daily ({settings?.currency_symbol || '£'}/box)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                className="h-8 text-sm"
                                value={settings?.storage_rate_per_box_daily || 5}
                                onChange={(e) => setSettings({...settings, storage_rate_per_box_daily: parseFloat(e.target.value) || 0})}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Weekly ({settings?.currency_symbol || '£'}/box)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                className="h-8 text-sm"
                                value={settings?.storage_rate_per_box_weekly || 25}
                                onChange={(e) => setSettings({...settings, storage_rate_per_box_weekly: parseFloat(e.target.value) || 0})}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Monthly ({settings?.currency_symbol || '£'}/box)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                className="h-8 text-sm"
                                value={settings?.storage_rate_per_box_monthly || 80}
                                onChange={(e) => setSettings({...settings, storage_rate_per_box_monthly: parseFloat(e.target.value) || 0})}
                              />
                            </div>
                          </div>
                          <div className="p-2 bg-blue-50 rounded text-xs text-blue-700">
                            <strong>Example:</strong> 20m³ needs {Math.ceil(20 / (settings?.storage_box_volume_m3 || 7))} boxes. 
                            1 month = {settings?.currency_symbol || '£'}{(Math.ceil(20 / (settings?.storage_box_volume_m3 || 7)) * (settings?.storage_rate_per_box_monthly || 80)).toFixed(2)}
                          </div>
                        </div>
                      )}
                      
                      {/* Per Volume Rates - shown when per_volume selected */}
                      {settings?.storage_pricing_model === 'per_volume' && (
                        <div className="p-4 bg-white rounded-lg border space-y-4">
                          <div className="flex items-center gap-2 text-sm font-medium text-green-700">
                            <Calculator size={16} />
                            Per Volume Rates (charge by actual m³)
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <Label>Daily ({settings?.currency_symbol || '£'}/m³)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={settings?.storage_rate_per_m3_daily || 1}
                                onChange={(e) => setSettings({...settings, storage_rate_per_m3_daily: parseFloat(e.target.value) || 0})}
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label>Weekly ({settings?.currency_symbol || '£'}/m³)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={settings?.storage_rate_per_m3_weekly || 5}
                                onChange={(e) => setSettings({...settings, storage_rate_per_m3_weekly: parseFloat(e.target.value) || 0})}
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label>Monthly ({settings?.currency_symbol || '£'}/m³)</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={settings?.storage_rate_per_m3_monthly || 15}
                                onChange={(e) => setSettings({...settings, storage_rate_per_m3_monthly: parseFloat(e.target.value) || 0})}
                                className="mt-1"
                              />
                            </div>
                          </div>
                          <div className="p-2 bg-green-50 rounded text-sm text-green-700">
                            <strong>Example:</strong> 20m³ for 1 month = {settings?.currency_symbol || '£'}{(20 * (settings?.storage_rate_per_m3_monthly || 15)).toFixed(2)}
                          </div>
                        </div>
                      )}
                      
                      {/* Per Container Rates - shown when per_container selected */}
                      {settings?.storage_pricing_model === 'per_container' && (
                        <div className="p-4 bg-white rounded-lg border space-y-4">
                          <div className="flex items-center gap-2 text-sm font-medium text-orange-700">
                            <Truck size={16} />
                            Fixed Container Sizes
                          </div>
                          {(settings?.storage_container_sizes || []).length > 0 ? (
                            <div className="space-y-2">
                              {settings.storage_container_sizes.map((container, idx) => (
                                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                                  <div className="flex justify-between items-center mb-2">
                                    <span className="font-medium">{container.name}</span>
                                    <Badge variant="outline">{container.volume_m3} m³</Badge>
                                  </div>
                                  <div className="grid grid-cols-3 gap-2 text-sm">
                                    <div><span className="text-gray-500">Daily:</span> {settings?.currency_symbol || '£'}{container.daily}</div>
                                    <div><span className="text-gray-500">Weekly:</span> {settings?.currency_symbol || '£'}{container.weekly}</div>
                                    <div><span className="text-gray-500">Monthly:</span> {settings?.currency_symbol || '£'}{container.monthly}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4 text-gray-500">
                              <p>No container sizes configured yet.</p>
                              <p className="text-xs mt-1">Contact support to set up your container options.</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Free days & minimum days */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Free Storage Days</Label>
                        <Input
                          type="number"
                          value={settings?.storage_free_days || 0}
                          onChange={(e) => setSettings({...settings, storage_free_days: parseInt(e.target.value) || 0})}
                          className="mt-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">First X days at no charge</p>
                      </div>
                      <div>
                        <Label>Minimum Billable Days</Label>
                        <Input
                          type="number"
                          value={settings?.storage_minimum_days || 1}
                          onChange={(e) => setSettings({...settings, storage_minimum_days: parseInt(e.target.value) || 1})}
                          className="mt-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">Minimum storage period to charge</p>
                      </div>
                    </div>
                    
                    <Separator className="my-4" />
                    
                    {/* Storage Handling Charges */}
                    <div className="p-4 border rounded-lg bg-orange-50">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium flex items-center gap-2">
                          <Truck size={18} />
                          Storage Handling Charges
                        </h4>
                        <Switch
                          checked={settings?.storage_handling_enabled ?? true}
                          onCheckedChange={(val) => setSettings({...settings, storage_handling_enabled: val})}
                        />
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        Additional charges for unloading goods INTO storage and reloading OUT of storage
                      </p>
                      
                      {settings?.storage_handling_enabled && (
                        <>
                          <div className="flex items-center gap-3 mb-3">
                            <Switch
                              checked={settings?.storage_handling_per_volume ?? false}
                              onCheckedChange={(val) => setSettings({...settings, storage_handling_per_volume: val})}
                            />
                            <span className="text-sm">Charge per cubic meter (instead of fixed rate)</span>
                          </div>
                          
                          {settings?.storage_handling_per_volume ? (
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label>Unload into Storage ({settings?.currency_symbol || '£'}/m³)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={settings?.storage_unload_rate_per_m3 || 10}
                                  onChange={(e) => setSettings({...settings, storage_unload_rate_per_m3: parseFloat(e.target.value) || 0})}
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label>Reload from Storage ({settings?.currency_symbol || '£'}/m³)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={settings?.storage_reload_rate_per_m3 || 10}
                                  onChange={(e) => setSettings({...settings, storage_reload_rate_per_m3: parseFloat(e.target.value) || 0})}
                                  className="mt-1"
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label>Unload into Storage ({settings?.currency_symbol || '£'} fixed)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={settings?.storage_unload_charge || 150}
                                  onChange={(e) => setSettings({...settings, storage_unload_charge: parseFloat(e.target.value) || 0})}
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label>Reload from Storage ({settings?.currency_symbol || '£'} fixed)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={settings?.storage_reload_charge || 150}
                                  onChange={(e) => setSettings({...settings, storage_reload_charge: parseFloat(e.target.value) || 0})}
                                  className="mt-1"
                                />
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    
                    <Separator className="my-4" />
                    
                    {/* Storage Facility Location */}
                    <div className="p-4 border rounded-lg bg-blue-50">
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <MapPin size={18} />
                        Storage Facility Location
                      </h4>
                      <p className="text-sm text-gray-600 mb-3">
                        Set your storage facility address for multi-leg distance calculation: Pickup → Storage → Delivery
                      </p>
                      <div className="space-y-3">
                        <div>
                          <Label>Facility Name</Label>
                          <Input
                            value={settings?.storage_facility_name || ''}
                            onChange={(e) => setSettings({...settings, storage_facility_name: e.target.value})}
                            placeholder="e.g., Main Storage Depot"
                            className="mt-1"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Street Address</Label>
                            <Input
                              value={settings?.storage_facility_address?.street || ''}
                              onChange={(e) => setSettings({
                                ...settings, 
                                storage_facility_address: {...(settings?.storage_facility_address || {}), street: e.target.value}
                              })}
                              placeholder="123 Storage Lane"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label>City</Label>
                            <Input
                              value={settings?.storage_facility_address?.city || ''}
                              onChange={(e) => setSettings({
                                ...settings, 
                                storage_facility_address: {...(settings?.storage_facility_address || {}), city: e.target.value}
                              })}
                              placeholder="Leicester"
                              className="mt-1"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Postal Code</Label>
                            <Input
                              value={settings?.storage_facility_address?.postal_code || ''}
                              onChange={(e) => setSettings({
                                ...settings, 
                                storage_facility_address: {...(settings?.storage_facility_address || {}), postal_code: e.target.value}
                              })}
                              placeholder="LE1 1AA"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label>Country</Label>
                            <Input
                              value={settings?.storage_facility_address?.country || 'UK'}
                              onChange={(e) => setSettings({
                                ...settings, 
                                storage_facility_address: {...(settings?.storage_facility_address || {}), country: e.target.value}
                              })}
                              className="mt-1"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
            
            {/* About Storage Pricing - shown when disabled */}
            {!settings?.storage_pricing_enabled && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 size={20} />
                    About Storage Pricing
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-gray-600">
                    When enabled, storage costs can be automatically calculated based on:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-sm text-gray-600">
                    <li><strong>Per Box:</strong> Standard storage containers (e.g., 7m³ wooden boxes) - charge per box used</li>
                    <li><strong>Per Volume:</strong> Direct charge per cubic meter of goods stored</li>
                    <li><strong>Per Container:</strong> Fixed-size storage units (Small/Medium/Large)</li>
                  </ul>
                  <p className="text-gray-600 text-sm">
                    Storage is calculated when the job has storage dates set. Customers who have their own storage will not be charged.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Labour Rates Tab */}
        <TabsContent value="labour">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users size={20} />
                  Labour Rates
                </CardTitle>
                <CardDescription>Configure hourly rates for different scenarios</CardDescription>
              </div>
              <Button onClick={() => setShowLabourDialog(true)} data-testid="add-labour-btn">
                <Plus size={16} className="mr-2" />
                Add Rate
              </Button>
            </CardHeader>
            <CardContent>
              {settings?.labour_rates?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="mx-auto mb-4 opacity-50" size={48} />
                  <p>No labour rates configured</p>
                  <Button variant="outline" className="mt-4" onClick={() => setShowLabourDialog(true)}>
                    Add Your First Rate
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {settings?.labour_rates?.map((rate) => (
                    <div key={rate.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Clock className="text-blue-600" size={20} />
                        </div>
                        <div>
                          <p className="font-medium">{rate.name}</p>
                          <p className="text-sm text-gray-500">{rate.description || `Min ${rate.min_hours} hours`}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-xl font-bold">{settings?.currency_symbol || '£'}{rate.rate_per_hour}/hr</p>
                          {rate.is_default && <Badge variant="outline" className="text-xs">Default</Badge>}
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => deleteLabourRate(rate.id)}>
                          <Trash2 className="text-red-500" size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Additional Charges Tab */}
        <TabsContent value="charges">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Additional Charges Card */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Truck size={20} />
                    Additional Charges
                  </CardTitle>
                  <CardDescription>Extra fees for special services</CardDescription>
                </div>
                <Button onClick={() => setShowChargeDialog(true)} size="sm">
                  <Plus size={16} className="mr-1" />
                  Add
                </Button>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  {settings?.additional_charges?.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <DollarSign className="mx-auto mb-4 opacity-50" size={48} />
                      <p>No additional charges</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {settings?.additional_charges?.map((charge) => (
                        <div key={charge.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <p className="font-medium">{charge.name}</p>
                            <p className="text-xs text-gray-500">{charge.charge_type}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold">{settings?.currency_symbol || '£'}{charge.amount}</span>
                            <Button variant="ghost" size="icon" onClick={() => deleteCharge(charge.id)}>
                              <Trash2 className="text-red-500" size={14} />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Custom Products Card */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 size={20} />
                    Custom Products & Services
                  </CardTitle>
                  <CardDescription>Additional items to add to estimates</CardDescription>
                </div>
                <Button onClick={() => setShowProductDialog(true)} size="sm">
                  <Plus size={16} className="mr-1" />
                  Add
                </Button>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  {settings?.custom_products?.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Package className="mx-auto mb-4 opacity-50" size={48} />
                      <p>No custom products</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {settings?.custom_products?.map((product) => (
                        <div key={product.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <p className="font-medium">{product.name}</p>
                            <Badge variant="outline" className="text-xs">{product.category}</Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold">{settings?.currency_symbol || '£'}{product.price}</span>
                            <Button variant="ghost" size="icon" onClick={() => deleteProduct(product.id)}>
                              <Trash2 className="text-red-500" size={14} />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Packing Materials Tab */}
        <TabsContent value="materials">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Package size={20} />
                  Packing Materials & Packs
                </CardTitle>
                <CardDescription>Pre-configured packing packages and supplies</CardDescription>
              </div>
              <Button onClick={() => setShowMaterialDialog(true)} data-testid="add-material-btn">
                <Plus size={16} className="mr-2" />
                Add Material Pack
              </Button>
            </CardHeader>
            <CardContent>
              {settings?.packing_materials?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="mx-auto mb-4 opacity-50" size={48} />
                  <p>No packing materials configured</p>
                  <Button variant="outline" className="mt-4" onClick={() => setShowMaterialDialog(true)}>
                    Add Your First Pack
                  </Button>
                </div>
              ) : (
                <div className="grid md:grid-cols-3 gap-4">
                  {settings?.packing_materials?.map((material) => (
                    <Card key={material.id} className="relative">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-2 right-2"
                        onClick={() => deleteMaterial(material.id)}
                      >
                        <Trash2 className="text-red-500" size={14} />
                      </Button>
                      <CardContent className="pt-6">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="p-2 bg-green-100 rounded-lg">
                            <Package className="text-green-600" size={24} />
                          </div>
                          <div>
                            <p className="font-semibold">{material.name}</p>
                            <p className="text-xl font-bold text-green-600">{settings?.currency_symbol || '£'}{material.price}</p>
                          </div>
                        </div>
                        {material.includes?.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-xs text-gray-500 mb-2">Includes:</p>
                            <div className="flex flex-wrap gap-1">
                              {material.includes.map((item, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">{item}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Messages Tab */}
        <TabsContent value="messages">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare size={20} />
                  Messages & Disclaimers
                </CardTitle>
                <CardDescription>Standard text to include on estimates (terms, notes, disclaimers)</CardDescription>
              </div>
              <Button onClick={() => setShowMessageDialog(true)} data-testid="add-message-btn">
                <Plus size={16} className="mr-2" />
                Add Message
              </Button>
            </CardHeader>
            <CardContent>
              {settings?.estimate_messages?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="mx-auto mb-4 opacity-50" size={48} />
                  <p>No messages configured</p>
                  <Button variant="outline" className="mt-4" onClick={() => setShowMessageDialog(true)}>
                    Add Your First Message
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {settings?.estimate_messages?.map((message) => (
                    <Card key={message.id} className="relative">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-2 right-2"
                        onClick={() => deleteMessage(message.id)}
                      >
                        <Trash2 className="text-red-500" size={14} />
                      </Button>
                      <CardHeader className="pb-2">
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-base">{message.title}</CardTitle>
                          <Badge variant="outline">{message.message_type}</Badge>
                          {message.is_default && (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle size={12} className="mr-1" />
                              Auto-attach
                            </Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{message.content}</p>
                        <p className="text-xs text-gray-400 mt-2">Position: {message.position}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment Methods Tab */}
        <TabsContent value="payments">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard size={20} />
                  Accepted Payment Methods
                </CardTitle>
                <CardDescription>Payment options displayed on all estimates</CardDescription>
              </div>
              <Button onClick={() => { setEditingPaymentId(null); setPaymentForm({ name: '', icon: 'credit-card', description: '', instructions: '' }); setShowPaymentDialog(true); }} data-testid="add-payment-btn">
                <Plus size={16} className="mr-2" />
                Add Payment Method
              </Button>
            </CardHeader>
            <CardContent>
              {settings?.payment_methods?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CreditCard className="mx-auto mb-4 opacity-50" size={48} />
                  <p>No payment methods configured</p>
                  <Button variant="outline" className="mt-4" onClick={() => setShowPaymentDialog(true)}>
                    Add Payment Method
                  </Button>
                </div>
              ) : (
                <div className="grid md:grid-cols-3 gap-4">
                  {settings?.payment_methods?.map((method) => (
                    <Card key={method.id} className="relative group">
                      <div className="absolute top-2 right-2 flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => openEditPaymentDialog(method)}
                          data-testid={`edit-payment-${method.id}`}
                        >
                          <Edit className="text-blue-500" size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => deletePaymentMethod(method.id)}
                        >
                          <Trash2 className="text-red-500" size={14} />
                        </Button>
                      </div>
                      <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                          <div className="p-3 bg-purple-100 rounded-lg">
                            <CreditCard className="text-purple-600" size={24} />
                          </div>
                          <div>
                            <p className="font-semibold">{method.name}</p>
                            {method.description && (
                              <p className="text-sm text-gray-500">{method.description}</p>
                            )}
                          </div>
                        </div>
                        {method.instructions && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-xs text-gray-500 whitespace-pre-wrap">{method.instructions}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Packing Services Tab */}
        <TabsContent value="packing">
          {!settings ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package size={20} />
                  Packing Service Rates
                </CardTitle>
                <CardDescription>
                  Set your per-item packing charges. These are applied when customers select "Requires Packing" on inventory items.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    <strong>💡 How it works:</strong> When a customer marks an item as "Requires Professional Packing" in the mobile app, the charges below are added to their estimate.
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Per Item Packing Fee</p>
                      <p className="text-sm text-gray-500">Base charge for packing each item</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">£</span>
                      <Input
                        type="number"
                        step="0.01"
                        className="w-24 text-right"
                        value={settings?.packing_per_item_fee || 5}
                        onChange={(e) => {
                          setSettings(prev => ({ ...prev, packing_per_item_fee: parseFloat(e.target.value) || 0 }));
                        }}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Fragile Item Surcharge</p>
                      <p className="text-sm text-gray-500">Extra charge for fragile items</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">£</span>
                      <Input
                        type="number"
                        step="0.01"
                        className="w-24 text-right"
                        value={settings?.packing_fragile_surcharge || 3}
                        onChange={(e) => {
                          setSettings(prev => ({ ...prev, packing_fragile_surcharge: parseFloat(e.target.value) || 0 }));
                        }}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Large Item Surcharge</p>
                      <p className="text-sm text-gray-500">Extra for sofas, mattresses, etc.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">£</span>
                      <Input
                        type="number"
                        step="0.01"
                        className="w-24 text-right"
                        value={settings?.packing_large_item_surcharge || 10}
                        onChange={(e) => {
                          setSettings(prev => ({ ...prev, packing_large_item_surcharge: parseFloat(e.target.value) || 0 }));
                        }}
                      />
                    </div>
                  </div>
                </div>

                <Button onClick={saveGeneralSettings} disabled={saving} className="w-full">
                  {saving ? 'Saving...' : 'Save Packing Rates'}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock size={20} />
                  Packing Time Estimates
                </CardTitle>
                <CardDescription>
                  Average time to pack items (used for labour calculations)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Small Item</p>
                      <p className="text-sm text-gray-500">Books, ornaments, small electronics</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="1"
                        className="w-20 text-right"
                        value={settings?.packing_time_small || 2}
                        onChange={(e) => {
                          setSettings(prev => ({ ...prev, packing_time_small: parseInt(e.target.value) || 0 }));
                        }}
                      />
                      <span className="text-gray-500 text-sm">mins</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Medium Item</p>
                      <p className="text-sm text-gray-500">Lamps, monitors, kitchen appliances</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="1"
                        className="w-20 text-right"
                        value={settings?.packing_time_medium || 5}
                        onChange={(e) => {
                          setSettings(prev => ({ ...prev, packing_time_medium: parseInt(e.target.value) || 0 }));
                        }}
                      />
                      <span className="text-gray-500 text-sm">mins</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Large Item</p>
                      <p className="text-sm text-gray-500">Sofas, wardrobes, mattresses</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="1"
                        className="w-20 text-right"
                        value={settings?.packing_time_large || 15}
                        onChange={(e) => {
                          setSettings(prev => ({ ...prev, packing_time_large: parseInt(e.target.value) || 0 }));
                        }}
                      />
                      <span className="text-gray-500 text-sm">mins</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Fragile Item</p>
                      <p className="text-sm text-gray-500">Glass, artwork, antiques</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        step="1"
                        className="w-20 text-right"
                        value={settings?.packing_time_fragile || 10}
                        onChange={(e) => {
                          setSettings(prev => ({ ...prev, packing_time_fragile: parseInt(e.target.value) || 0 }));
                        }}
                      />
                      <span className="text-gray-500 text-sm">mins</span>
                    </div>
                  </div>
                </div>

                <Button onClick={saveGeneralSettings} disabled={saving} className="w-full">
                  {saving ? 'Saving...' : 'Save Time Estimates'}
                </Button>
              </CardContent>
            </Card>
          </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Labour Rate Dialog */}
      <Dialog open={showLabourDialog} onOpenChange={setShowLabourDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Labour Rate</DialogTitle>
            <DialogDescription>Configure a new hourly rate option</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Rate Name</Label>
              <Input
                placeholder="e.g., Standard Weekday Rate"
                value={labourForm.name}
                onChange={(e) => setLabourForm({...labourForm, name: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Rate per Hour ({settings?.currency_symbol || '£'})</Label>
                <Input
                  type="number"
                  value={labourForm.rate_per_hour}
                  onChange={(e) => setLabourForm({...labourForm, rate_per_hour: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Minimum Hours</Label>
                <Input
                  type="number"
                  value={labourForm.min_hours}
                  onChange={(e) => setLabourForm({...labourForm, min_hours: parseFloat(e.target.value) || 2})}
                />
              </div>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input
                placeholder="e.g., Applicable Mon-Fri 9am-5pm"
                value={labourForm.description}
                onChange={(e) => setLabourForm({...labourForm, description: e.target.value})}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={labourForm.is_default}
                onCheckedChange={(val) => setLabourForm({...labourForm, is_default: val})}
              />
              <Label>Set as default rate</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLabourDialog(false)}>Cancel</Button>
            <Button onClick={addLabourRate}>Add Rate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Charge Dialog */}
      <Dialog open={showChargeDialog} onOpenChange={setShowChargeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Additional Charge</DialogTitle>
            <DialogDescription>Add a new service charge or fee</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Charge Name</Label>
              <Input
                placeholder="e.g., Stair Carry Fee"
                value={chargeForm.name}
                onChange={(e) => setChargeForm({...chargeForm, name: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount ({settings?.currency_symbol || '£'})</Label>
                <Input
                  type="number"
                  value={chargeForm.amount}
                  onChange={(e) => setChargeForm({...chargeForm, amount: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Charge Type</Label>
                <Select value={chargeForm.charge_type} onValueChange={(val) => setChargeForm({...chargeForm, charge_type: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Fixed Amount</SelectItem>
                    <SelectItem value="per_item">Per Item</SelectItem>
                    <SelectItem value="per_floor">Per Floor</SelectItem>
                    <SelectItem value="per_meter">Per Meter</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input
                placeholder="When this charge applies..."
                value={chargeForm.description}
                onChange={(e) => setChargeForm({...chargeForm, description: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChargeDialog(false)}>Cancel</Button>
            <Button onClick={addCharge}>Add Charge</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Material Dialog */}
      <Dialog open={showMaterialDialog} onOpenChange={setShowMaterialDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Packing Material Pack</DialogTitle>
            <DialogDescription>Create a pre-configured packing package</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Pack Name</Label>
              <Input
                placeholder="e.g., Moving Pack - 2 Bed House"
                value={materialForm.name}
                onChange={(e) => setMaterialForm({...materialForm, name: e.target.value})}
              />
            </div>
            <div>
              <Label>Price ({settings?.currency_symbol || '£'})</Label>
              <Input
                type="number"
                value={materialForm.price}
                onChange={(e) => setMaterialForm({...materialForm, price: parseFloat(e.target.value) || 0})}
              />
            </div>
            <div>
              <Label>What&apos;s Included (comma separated)</Label>
              <Textarea
                placeholder="10 boxes, Tape, Bubble wrap, Paper"
                value={materialForm.includes}
                onChange={(e) => setMaterialForm({...materialForm, includes: e.target.value})}
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input
                placeholder="Additional details..."
                value={materialForm.description}
                onChange={(e) => setMaterialForm({...materialForm, description: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMaterialDialog(false)}>Cancel</Button>
            <Button onClick={addMaterial}>Add Pack</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Product Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Custom Product/Service</DialogTitle>
            <DialogDescription>Add a custom item to include in estimates</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Product Name</Label>
              <Input
                placeholder="e.g., Piano Moving Service"
                value={productForm.name}
                onChange={(e) => setProductForm({...productForm, name: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Price ({settings?.currency_symbol || '£'})</Label>
                <Input
                  type="number"
                  value={productForm.price}
                  onChange={(e) => setProductForm({...productForm, price: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={productForm.category} onValueChange={(val) => setProductForm({...productForm, category: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="product">Product</SelectItem>
                    <SelectItem value="fee">Fee</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input
                placeholder="What this includes..."
                value={productForm.description}
                onChange={(e) => setProductForm({...productForm, description: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProductDialog(false)}>Cancel</Button>
            <Button onClick={addProduct}>Add Product</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Message Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Message/Disclaimer</DialogTitle>
            <DialogDescription>Add standard text to include on estimates</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Title</Label>
              <Input
                placeholder="e.g., Terms & Conditions"
                value={messageForm.title}
                onChange={(e) => setMessageForm({...messageForm, title: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Message Type</Label>
                <Select value={messageForm.message_type} onValueChange={(val) => setMessageForm({...messageForm, message_type: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard Note</SelectItem>
                    <SelectItem value="disclaimer">Disclaimer</SelectItem>
                    <SelectItem value="terms">Terms & Conditions</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Position</Label>
                <Select value={messageForm.position} onValueChange={(val) => setMessageForm({...messageForm, position: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="header">Header (Top)</SelectItem>
                    <SelectItem value="footer">Footer (Bottom)</SelectItem>
                    <SelectItem value="terms_section">Terms Section</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Content</Label>
              <Textarea
                placeholder="Enter your message text..."
                rows={5}
                value={messageForm.content}
                onChange={(e) => setMessageForm({...messageForm, content: e.target.value})}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={messageForm.is_default}
                onCheckedChange={(val) => setMessageForm({...messageForm, is_default: val})}
              />
              <Label>Automatically attach to all estimates</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMessageDialog(false)}>Cancel</Button>
            <Button onClick={addMessage}>Add Message</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Payment Method Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={closePaymentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingPaymentId ? 'Edit Payment Method' : 'Add Payment Method'}</DialogTitle>
            <DialogDescription>{editingPaymentId ? 'Update payment method details' : 'Add an accepted payment option'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Payment Method Name</Label>
              <Input
                placeholder="e.g., Bank Transfer"
                value={paymentForm.name}
                onChange={(e) => setPaymentForm({...paymentForm, name: e.target.value})}
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input
                placeholder="e.g., BACS payment"
                value={paymentForm.description}
                onChange={(e) => setPaymentForm({...paymentForm, description: e.target.value})}
              />
            </div>
            <div>
              <Label>Instructions (optional)</Label>
              <Textarea
                placeholder="e.g., Bank: Barclays&#10;Sort Code: 12-34-56&#10;Account: 12345678"
                rows={4}
                value={paymentForm.instructions}
                onChange={(e) => setPaymentForm({...paymentForm, instructions: e.target.value})}
              />
              <p className="text-xs text-gray-500 mt-1">Include bank details or other payment instructions</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closePaymentDialog}>Cancel</Button>
            <Button onClick={editingPaymentId ? updatePaymentMethod : addPaymentMethod}>
              {editingPaymentId ? 'Update Method' : 'Add Method'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Estimate Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye size={20} />
              Estimate Preview
            </DialogTitle>
            <DialogDescription>
              This is how your estimates will appear to customers
            </DialogDescription>
          </DialogHeader>
          
          {previewData && (
            <div className="border rounded-lg p-6 bg-white shadow-sm" id="estimate-preview">
              {/* Header */}
              <div className="flex justify-between items-start mb-6 pb-4 border-b">
                <div>
                  {previewData.show_company_logo && (
                    <div className="w-24 h-12 bg-gray-100 rounded flex items-center justify-center mb-2">
                      <Building2 className="text-gray-400" size={24} />
                    </div>
                  )}
                  <h2 className="text-xl font-bold">{previewData.company?.name || 'Your Company'}</h2>
                  {previewData.company?.address && (
                    <p className="text-sm text-gray-600">{previewData.company.address}</p>
                  )}
                </div>
                <div className="text-right">
                  <h3 className="text-2xl font-bold text-primary">ESTIMATE</h3>
                  <p className="text-sm text-gray-600">{previewData.estimate_number}</p>
                  <p className="text-sm text-gray-600">Date: {previewData.date}</p>
                  <p className="text-sm text-gray-600">Valid Until: {previewData.valid_until}</p>
                </div>
              </div>

              {/* Customer & Job Details */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h4 className="font-semibold text-gray-500 text-sm mb-2">BILL TO</h4>
                  <p className="font-medium">{previewData.customer?.first_name} {previewData.customer?.last_name}</p>
                  {previewData.customer?.email && (
                    <p className="text-sm text-gray-600 flex items-center gap-1">
                      <Mail size={14} /> {previewData.customer.email}
                    </p>
                  )}
                  {previewData.customer?.phone && (
                    <p className="text-sm text-gray-600 flex items-center gap-1">
                      <Phone size={14} /> {previewData.customer.phone}
                    </p>
                  )}
                </div>
                <div>
                  <h4 className="font-semibold text-gray-500 text-sm mb-2">JOB DETAILS</h4>
                  <p className="text-sm"><span className="font-medium">Ref:</span> {previewData.job_details?.reference}</p>
                  <p className="text-sm flex items-start gap-1">
                    <MapPin size={14} className="mt-0.5 text-green-600" />
                    <span><strong>From:</strong> {previewData.job_details?.from_address}</span>
                  </p>
                  <p className="text-sm flex items-start gap-1">
                    <MapPin size={14} className="mt-0.5 text-red-600" />
                    <span><strong>To:</strong> {previewData.job_details?.to_address}</span>
                  </p>
                </div>
              </div>

              <Separator className="my-4" />

              {/* Items Breakdown */}
              {previewData.show_items && previewData.items?.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold mb-3">Items to Move</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      {previewData.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between">
                          <span>{item.name}</span>
                          <span className="text-gray-500">x{item.quantity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Labour Breakdown */}
              {previewData.show_labour && previewData.labour && (
                <div className="mb-6">
                  <h4 className="font-semibold mb-3">Labour</h4>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Description</th>
                        <th className="text-right py-2">Rate</th>
                        <th className="text-right py-2">Hours</th>
                        <th className="text-right py-2">Crew</th>
                        <th className="text-right py-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="py-2">{previewData.labour.rate_name}</td>
                        <td className="text-right">{previewData.pricing?.currency}{previewData.labour.hourly_rate}/hr</td>
                        <td className="text-right">{previewData.labour.hours}</td>
                        <td className="text-right">{previewData.labour.crew_size}</td>
                        <td className="text-right font-medium">{previewData.pricing?.currency}{previewData.labour.total?.toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* Additional Charges Available */}
              {previewData.additional_charges?.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold mb-3">Additional Services Available</h4>
                  <div className="grid md:grid-cols-2 gap-2 text-sm">
                    {previewData.additional_charges.map((charge, idx) => (
                      <div key={idx} className="flex justify-between p-2 bg-gray-50 rounded">
                        <span>{charge.name}</span>
                        <span className="font-medium">{previewData.pricing?.currency}{charge.amount} ({charge.charge_type})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Packing Materials Available */}
              {previewData.packing_materials?.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold mb-3">Packing Materials Available</h4>
                  <div className="grid md:grid-cols-2 gap-2 text-sm">
                    {previewData.packing_materials.map((material, idx) => (
                      <div key={idx} className="flex justify-between p-2 bg-green-50 rounded">
                        <span>{material.name}</span>
                        <span className="font-medium text-green-700">{previewData.pricing?.currency}{material.price}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Separator className="my-4" />

              {/* Pricing Summary */}
              <div className="flex justify-end">
                <div className="w-64">
                  <div className="flex justify-between py-1">
                    <span>Subtotal</span>
                    <span>{previewData.pricing?.currency}{previewData.pricing?.subtotal?.toFixed(2)}</span>
                  </div>
                  {previewData.pricing?.vat_rate > 0 && (
                    <div className="flex justify-between py-1 text-gray-600">
                      <span>VAT ({previewData.pricing.vat_rate}%)</span>
                      <span>{previewData.pricing?.currency}{previewData.pricing?.vat_amount?.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-2 border-t font-bold text-lg">
                    <span>Total</span>
                    <span className="text-primary">{previewData.pricing?.currency}{previewData.pricing?.total?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-1 text-sm bg-primary/10 px-2 rounded">
                    <span>Deposit ({previewData.pricing?.deposit_percentage}%)</span>
                    <span className="font-medium">{previewData.pricing?.currency}{previewData.pricing?.deposit_amount?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-1 text-sm">
                    <span>Balance Due</span>
                    <span>{previewData.pricing?.currency}{previewData.pricing?.balance_due?.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Payment Methods */}
              {previewData.show_payment_methods && previewData.payment_methods?.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h4 className="font-semibold mb-3">Accepted Payment Methods</h4>
                  <div className="flex flex-wrap gap-3">
                    {previewData.payment_methods.map((method, idx) => (
                      <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
                        <CreditCard size={16} className="text-gray-600" />
                        <span className="text-sm font-medium">{method.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Messages/Terms */}
              {previewData.messages?.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  {previewData.messages.map((msg, idx) => (
                    <div key={idx} className="mb-4">
                      <h5 className="font-semibold text-sm text-gray-500">{msg.title}</h5>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>Close</Button>
            <Button onClick={() => window.print()}>
              <Printer size={16} className="mr-2" />
              Print Preview
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EstimateSettings;
